/**
 * Privacy Policy Page
 * ====================
 * Public page - accessible without authentication
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Database, UserCheck, Globe, Mail } from 'lucide-react';
import NearULogo from '../components/NearULogo';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  const sections = [
    {
      icon: Database,
      title: 'Information We Collect',
      content: `We collect information you provide directly to us, including:
      
• Account information (name, email, phone number, date of birth)
• Profile information (photos, bio, interests, preferences)
• Location data when you use proximity features
• Communications with other members
• Payment information for premium services
• Device information and usage data`
    },
    {
      icon: Eye,
      title: 'How We Use Your Information',
      content: `We use the information we collect to:
      
• Provide, maintain, and improve our services
• Match you with compatible members
• Process transactions and send related information
• Send promotional communications (with your consent)
• Monitor and analyze trends and usage
• Detect, investigate, and prevent fraud and abuse
• Comply with legal obligations`
    },
    {
      icon: UserCheck,
      title: 'Information Sharing',
      content: `We may share your information:
      
• With other members as part of the service (profile visibility)
• With service providers who assist our operations
• In response to legal requests or to protect rights
• In connection with a merger, acquisition, or sale
• With your consent or at your direction

We never sell your personal information to third parties.`
    },
    {
      icon: Lock,
      title: 'Data Security',
      content: `We implement appropriate security measures to protect your information:
      
• Encryption of data in transit and at rest
• Regular security assessments and audits
• Access controls and authentication
• Secure data centers with physical security
• Employee training on data protection

While we strive to protect your information, no method of transmission over the Internet is 100% secure.`
    },
    {
      icon: Globe,
      title: 'Your Rights and Choices',
      content: `You have the right to:
      
• Access, update, or delete your personal information
• Opt out of promotional communications
• Control location sharing and proximity features
• Request a copy of your data
• Withdraw consent where applicable
• Lodge a complaint with a supervisory authority

To exercise these rights, contact us at privacy@nearu.com`
    },
    {
      icon: Shield,
      title: 'Data Retention',
      content: `We retain your information for as long as your account is active or as needed to provide services. We may retain certain information:
      
• To comply with legal obligations
• To resolve disputes and enforce agreements
• For legitimate business purposes
• As required by applicable law

You may request deletion of your account at any time through the app settings.`
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0D0D0D 0%, #1A0F0A 50%, #0D0D0D 100%)',
      paddingBottom: '40px'
    }}>
      {/* Header */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        background: 'rgba(26, 15, 10, 0.95)',
        backdropFilter: 'blur(10px)',
        padding: '16px 20px',
        borderBottom: '1px solid rgba(165, 124, 51, 0.2)',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'none',
            border: 'none',
            color: '#E6D7C3',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <ArrowLeft size={24} />
        </button>
        <NearULogo size="small" showText={true} />
      </div>

      <div style={{ padding: '24px 20px', maxWidth: '800px', margin: '0 auto' }}>
        {/* Title */}
        <h1 style={{
          fontFamily: 'Cinzel, serif',
          fontSize: '28px',
          fontWeight: '600',
          color: '#E6D7C3',
          marginBottom: '8px',
          textAlign: 'center'
        }}>
          Privacy Policy
        </h1>
        
        <p style={{
          color: '#A57C33',
          fontSize: '14px',
          textAlign: 'center',
          marginBottom: '32px'
        }}>
          Last updated: December 2025
        </p>

        {/* Introduction */}
        <div style={{
          background: 'rgba(215, 181, 109, 0.08)',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '24px',
          border: '1px solid rgba(165, 124, 51, 0.2)'
        }}>
          <p style={{
            color: '#E6D7C3',
            fontSize: '15px',
            lineHeight: '1.7',
            margin: 0
          }}>
            At NearU, we take your privacy seriously. This Privacy Policy explains how we collect, 
            use, disclose, and safeguard your information when you use our mobile application and services. 
            Please read this policy carefully to understand our practices regarding your personal data.
          </p>
        </div>

        {/* Sections */}
        {sections.map((section, index) => {
          const Icon = section.icon;
          return (
            <div
              key={index}
              style={{
                background: 'rgba(26, 15, 10, 0.6)',
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '16px',
                border: '1px solid rgba(165, 124, 51, 0.15)'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, rgba(215, 181, 109, 0.2) 0%, rgba(165, 124, 51, 0.1) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Icon size={20} color="#D7B56D" />
                </div>
                <h2 style={{
                  fontFamily: 'Cinzel, serif',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#E6D7C3',
                  margin: 0
                }}>
                  {section.title}
                </h2>
              </div>
              <p style={{
                color: '#A57C33',
                fontSize: '14px',
                lineHeight: '1.8',
                margin: 0,
                whiteSpace: 'pre-line'
              }}>
                {section.content}
              </p>
            </div>
          );
        })}

        {/* Contact */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(215, 181, 109, 0.15) 0%, rgba(165, 124, 51, 0.08) 100%)',
          borderRadius: '16px',
          padding: '24px',
          textAlign: 'center',
          border: '1px solid rgba(215, 181, 109, 0.3)'
        }}>
          <Mail size={32} color="#D7B56D" style={{ marginBottom: '12px' }} />
          <h3 style={{
            fontFamily: 'Cinzel, serif',
            fontSize: '18px',
            color: '#E6D7C3',
            marginBottom: '8px'
          }}>
            Questions About Privacy?
          </h3>
          <p style={{
            color: '#A57C33',
            fontSize: '14px',
            marginBottom: '16px'
          }}>
            Contact our Privacy Team
          </p>
          <a
            href="mailto:privacy@nearu.com"
            style={{
              color: '#D7B56D',
              fontSize: '16px',
              fontWeight: '600',
              textDecoration: 'none'
            }}
          >
            privacy@nearu.com
          </a>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
